/**
 * HUD panels other than the queue bar (DESIGN 6.5): top-left wave /
 * enemies / timer / global magick icons, top-right ally cards, bottom-right
 * magick hint ("W A -> Revive (Space)"), the jotunn boss bar, ping + fps in the
 * corner, centre banners (wave start, magicks) and the centre status text
 * (countdown number, "You died" with the revive hint, breather).
 */

import { BOSS, MAGICKS, MIRE, MODES, type Element, type MagickId } from '@shared/index.js';
import type { LocalView, ViewModel } from '../state/viewModel.js';
import { elementColor, ELEMENT_SWATCH, hpColor, pingColor, playerColor, rgba, UI } from '../palette.js';
import { circlePath, clamp01, easeOutCubic, font, outlinedText, roundRectPath, type Ctx } from './gfx.js';
import { drawElementMark, drawStatusGlyph, drawTimeArc } from './glyphs.js';
import { drawStatusIcons } from './statusFx.js';
import { BADGE_R, BADGE_STEP, globalBadgesLayout, magickHintLayout } from './hudLayout.js';
import { magickKeys } from './hudQueue.js';
import { WOVEN_SHORT } from '../ui/wovenLabels.js';

/** Key letters per element for the magick hint (steam / ice as the two keys that make them). */
const KEY_OF: Readonly<Record<Element, string>> = {
  water: 'Q', life: 'W', shield: 'E', cold: 'R', lightning: 'A', arcane: 'S', earth: 'D', fire: 'F', steam: 'QF', ice: 'QR', mud: 'QD', lava: 'FD', spring: 'QW',
  ...WOVEN_SHORT,
};

/** `text` cut short with an ellipsis so it fits in `maxW` at the current font. */
function clipText(ctx: Ctx, text: string, maxW: number): string {
  if (ctx.measureText(text).width <= maxW) return text;
  let t = text;
  while (t.length > 1 && ctx.measureText(`${t}…`).width > maxW) t = t.slice(0, -1);
  return `${t.trimEnd()}…`;
}

interface Banner { text: string; sub: string; color: string; t: number; dur: number }

const BANNER_MAX = 3;

export class Panels {
  private readonly banners: Banner[] = [];
  private readonly ffFlash = new Map<number, number>();

  /** Centre banner: scales 2x -> 1x over 0.4 s, holds, fades in the last 0.5 s. */
  banner(text: string, sub: string, color: string, dur = 2.2): void {
    if (this.banners.length >= BANNER_MAX) this.banners.shift();
    this.banners.push({ text, sub, color, t: 0, dur });
  }

  /** Ally card flashes red: that ally just took friendly fire (DESIGN juice 3.5 tell). */
  flashAlly(pid: number): void { this.ffFlash.set(pid, 0.6); }

  step(dt: number): void {
    for (let i = this.banners.length - 1; i >= 0; i--) {
      const b = this.banners[i]!;
      b.t += dt;
      if (b.t >= b.dur) this.banners.splice(i, 1);
    }
    this.ffFlash.forEach((t, k) => { if (t - dt <= 0) this.ffFlash.delete(k); else this.ffFlash.set(k, t - dt); });
  }

  // ---------------------------------------------------------------- top-left: wave, enemies, timer, globals

  /** `sw` is the screen width and `touch` the phone layout, for where the global Magick badges go. */
  drawWave(ctx: Ctx, vm: ViewModel, s: number, sw: number, touch: boolean): void {
    const w = vm.wave;
    const x = 16 * s, y = 16 * s;
    const pw = 230 * s, ph = 74 * s;
    ctx.fillStyle = UI.panel;
    roundRectPath(ctx, x, y, pw, ph, 8 * s);
    ctx.fill();
    ctx.strokeStyle = UI.panelEdge;
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    let title: string, sub: string, color: string = UI.text;
    const phase = w ? w.phase : vm.phase;
    const story = vm.room?.mode === 'story';
    const ctf = vm.room?.mode === 'ctf';
    const sy = vm.story;
    switch (phase) {
      case 'lobby':
        title = 'LOBBY';
        sub = story ? `${MODES.story.blurb} Start when ready.` : ctf ? `${MODES.ctf.blurb} Pick a side.` : 'Training dummies. Cast at them.';
        break;
      case 'countdown':
        title = 'GET READY';
        sub = story ? `The act begins in ${w ? Math.ceil(w.t) : 0}s` : ctf ? `The match begins in ${w ? Math.ceil(w.t) : 0}s` : `Wave ${w ? w.n + 1 : 1} in ${w ? Math.ceil(w.t) : 0}s`;
        color = UI.gold;
        break;
      case 'wave': title = `WAVE ${w?.n ?? 0}`; sub = `${w?.alive ?? 0} / ${w?.total ?? 0} enemies left`; break;
      case 'breather': title = `WAVE ${w?.n ?? 0} CLEAR`; sub = `Next wave in ${w ? Math.ceil(w.t) : 0}s`; color = UI.hpGreen; break;
      case 'story':
        title = (sy?.ti ?? 'STORY').toUpperCase();
        if (sy && sy.t > 0 && sy.o !== '') { sub = `Standing up at the rune stone in ${Math.ceil(sy.t)}s`; color = UI.hpRed; }
        else if (sy && sy.o === '') { sub = 'The way on. Well done.'; color = UI.hpGreen; }
        else sub = sy?.o ? `▸ ${sy.o}` : '';
        break;
      case 'match': {
        title = 'CAPTURE THE FLAG';
        const me = vm.players.find((p) => p.isLocal);
        if (me?.dead && me.rs !== undefined) { sub = `Back at your base in ${Math.ceil(me.rs)}s`; color = UI.hpRed; }
        else if (me?.fl !== undefined) { sub = '▸ Take it home to your plinth'; color = UI.gold; }
        else sub = '▸ Bring their flag home while yours is home';
        break;
      }
      case 'gameover':
        title = story ? 'ACT OVER' : ctf ? 'MATCH OVER' : 'GAME OVER';
        sub = story ? (sy?.a ?? MODES.story.blurb) : ctf ? MODES.ctf.name : `Reached wave ${w?.n ?? 0}`;
        color = story ? UI.gold : UI.hpRed;
        break;
    }
    ctx.font = font(18 * s, 800);
    ctx.fillStyle = color;
    ctx.fillText(title, x + 12 * s, y + 20 * s);
    ctx.font = font(11 * s, 600);
    ctx.fillStyle = UI.muted;
    ctx.fillText(sub, x + 12 * s, y + 40 * s);
    // enemies-left bar during a wave
    if (w && phase === 'wave' && w.total > 0) {
      ctx.fillStyle = 'rgba(255,255,255,0.1)';
      ctx.fillRect(x + 12 * s, y + 54 * s, pw - 24 * s, 5 * s);
      ctx.fillStyle = UI.enemyHp;
      ctx.fillRect(x + 12 * s, y + 54 * s, (pw - 24 * s) * clamp01(w.alive / w.total), 5 * s);
    }
    // global magick icons: rain / blizzard / time warp with seconds left (hudLayout.globalBadgesLayout)
    if (w) {
      const all: { on: number; el: Element | null; status: 'slowed' | null; max: number; label: string }[] = [
        { on: w.rain, el: 'water', status: null, max: 17.5, label: 'Rain' },
        { on: w.blizzard, el: 'cold', status: null, max: 10, label: 'Blizzard' },
        { on: w.timewarp, el: null, status: 'slowed', max: 15, label: 'Time Warp' },
      ];
      const globals = all.filter((g) => g.on > 0);
      const at = globalBadgesLayout(sw, s, globals.length, ctf, touch);
      let gx = at.x;
      const gy = at.y, r = BADGE_R * s;
      for (const g of globals) {
        const col = g.el ? elementColor(g.el) : UI.muted;
        ctx.fillStyle = UI.panel;
        circlePath(ctx, gx, gy, r);
        ctx.fill();
        ctx.fillStyle = col;
        if (g.el) drawElementMark(ctx, g.el, gx, gy, r);
        else if (g.status) drawStatusGlyph(ctx, g.status, gx, gy, r);
        drawTimeArc(ctx, gx, gy, r, g.on / g.max, col, 2);
        // the label on a dark backing, so it reads over the rain's tint and the snow
        ctx.font = font(10 * s, 700);
        const label = `${g.label} ${Math.ceil(g.on)}s`;
        const lw = ctx.measureText(label).width + 10 * s;
        ctx.fillStyle = UI.panel;
        roundRectPath(ctx, gx - lw / 2, gy + 19 * s, lw, 14 * s, 4 * s);
        ctx.fill();
        ctx.fillStyle = col;
        ctx.textAlign = 'center';
        ctx.fillText(label, gx, gy + 26 * s);
        ctx.textAlign = 'left';
        gx += BADGE_STEP * s;
      }
    }
  }

  // ---------------------------------------------------------------- top-right: ally cards

  drawAllies(ctx: Ctx, vm: ViewModel, w: number, s: number, time: number): void {
    const cw = 210 * s, ch = 54 * s, gap = 6 * s;
    let y = 52 * s; // below the Book and settings buttons in the top-right corner
    const x = w - cw - 16 * s;
    let n = 0;
    for (const p of vm.players) {
      if (p.isLocal) continue;
      const col = playerColor(p.color);
      const flash = this.ffFlash.get(p.id) ?? 0;
      ctx.fillStyle = flash > 0 ? rgba(UI.ff, 0.25 + 0.35 * flash * Math.abs(Math.sin(time * 20))) : UI.panel;
      roundRectPath(ctx, x, y, cw, ch, 8 * s);
      ctx.fill();
      ctx.strokeStyle = p.dead ? rgba(UI.hpRed, 0.6) : UI.panelEdge;
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.globalAlpha = p.dead || p.con === 0 ? 0.6 : 1;
      // badge + name
      ctx.fillStyle = col;
      circlePath(ctx, x + 14 * s, y + 14 * s, 8 * s);
      ctx.fill();
      ctx.fillStyle = '#000000';
      ctx.font = font(10 * s, 800);
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(`${n + 2}`, x + 14 * s, y + 14.5 * s);
      ctx.textAlign = 'left';
      ctx.font = font(12 * s, 700);
      ctx.fillStyle = UI.text;
      ctx.fillText(p.name, x + 28 * s, y + 14 * s);
      if (p.dead) {
        ctx.font = font(11 * s, 800);
        ctx.fillStyle = UI.hpRed;
        ctx.textAlign = 'right';
        ctx.fillText('DEAD', x + cw - 10 * s, y + 14 * s);
        ctx.textAlign = 'left';
      } else if (p.con === 0) {
        ctx.font = font(10 * s, 700);
        ctx.fillStyle = UI.muted;
        ctx.textAlign = 'right';
        ctx.fillText('reconnecting', x + cw - 10 * s, y + 14 * s);
        ctx.textAlign = 'left';
      }
      // HP bar + text
      const bx = x + 28 * s, by = y + 26 * s, bw = 120 * s, bh = 8 * s;
      const frac = clamp01(p.hp / 1000);
      ctx.fillStyle = 'rgba(255,255,255,0.1)';
      ctx.fillRect(bx, by, bw, bh);
      ctx.fillStyle = hpColor(frac);
      ctx.fillRect(bx, by, bw * frac, bh);
      if (p.bub > 0) { ctx.fillStyle = rgba(ELEMENT_SWATCH.shield.core, 0.9); ctx.fillRect(bx, by, bw * clamp01(p.bub / 500), 2 * s); }
      ctx.font = font(10 * s, 700);
      ctx.fillStyle = UI.muted;
      ctx.fillText(`${Math.max(0, Math.round(p.hp))}`, bx + bw + 6 * s, by + bh / 2);
      // queue dots + status icons: the co-op tell (you can see an ally holding Lightning before you wet them)
      const qx = bx, qy = y + 40 * s;
      for (let i = 0; i < 5; i++) {
        const el = p.q[i];
        ctx.fillStyle = el ? elementColor(el) : 'rgba(255,255,255,0.14)';
        roundRectPath(ctx, qx + i * 13 * s, qy - 4 * s, 10 * s, 8 * s, 2 * s);
        ctx.fill();
      }
      drawStatusIcons(ctx, p.st, bx + 95 * s + Math.max(0, p.st.length - 1) * 7 * s, qy, 7 * s);
      ctx.globalAlpha = 1;
      y += ch + gap;
      n++;
    }
  }

  // ---------------------------------------------------------------- bottom-right: magick hint

  /**
   * The Magicks the queue is the start of, with the keys still to press and the key that casts
   * them (the bound keys, or the Magick button on a phone), or how long the PvP cooldown has left.
   * Sized to its text and placed by hudLayout.magickHintLayout, clear of the key rows and of a
   * phone's buttons.
   */
  drawMagickHint(ctx: Ctx, me: LocalView, w: number, h: number, s: number, touch: boolean): void {
    if (me.magickHint.length === 0) return;
    const ids: MagickId[] = me.magickHint.slice(0, 4);
    const rowH = 24 * s, pad = 10 * s;
    const keyFont = font(13 * s, 800), nameFont = font(13 * s, 600), readyFont = font(13 * s, 800);
    const how = me.magickCd ? `ready in ${Math.ceil(me.magickCd.left)} s` : magickKeys().join(' / ');
    const label = (id: MagickId): string => `${MAGICKS[id].name}  (${how})`;
    // the key column is as wide as the longest sequence ("QF F QF F QF"), so names line up after it
    ctx.font = keyFont;
    const seqW = (id: MagickId): number => MAGICKS[id].seq.reduce((sum, el) => sum + ctx.measureText(KEY_OF[el]).width + 5 * s, 0);
    const keysW = Math.max(...ids.map(seqW)) + 8 * s;
    ctx.font = readyFont;
    const textW = Math.max(...ids.map((id) => ctx.measureText(label(id)).width));
    const ph = ids.length * rowH + 12 * s;
    const box = magickHintLayout(w, h, s, pad + keysW + textW + pad, ph, touch);
    const { x, y } = box;
    const pw = Math.min(pad + keysW + textW + pad, box.maxW);
    const roomForText = pw - pad - keysW - pad;
    ctx.fillStyle = 'rgba(16,18,28,0.9)';
    roundRectPath(ctx, x, y, pw, ph, 8 * s);
    ctx.fill();
    ctx.strokeStyle = me.magickCd ? UI.panelEdge : rgba(UI.gold, 0.35);
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'left';
    let ry = y + 6 * s + rowH / 2;
    for (const id of ids) {
      const m = MAGICKS[id];
      const ready = m.seq.length === me.queue.length;
      let tx = x + pad;
      ctx.font = keyFont;
      m.seq.forEach((el, i) => {
        const typed = i < me.queue.length;
        ctx.fillStyle = typed ? elementColor(el) : rgba(elementColor(el), 0.45);
        const key = KEY_OF[el];
        ctx.fillText(key, tx, ry);
        tx += ctx.measureText(key).width + 5 * s;
      });
      ctx.fillStyle = ready && !me.magickCd ? UI.gold : ready ? UI.text : UI.muted;
      ctx.font = ready ? readyFont : nameFont;
      ctx.fillText(clipText(ctx, label(id), roomForText), x + pad + keysW, ry);
      ry += rowH;
    }
  }

  // ---------------------------------------------------------------- boss bar, ping / fps

  drawBossBar(ctx: Ctx, vm: ViewModel, w: number, s: number): void {
    const boss = vm.enemies.find((e) => e.boss);
    if (!boss) return;
    if (boss.k === 'mire') {
      this.drawMireBar(ctx, boss, vm.terrain?.bo === 1, w, s);
      return;
    }
    const bw = 420 * s, bh = 14 * s;
    const x = w / 2 - bw / 2, y = 18 * s;
    ctx.fillStyle = UI.panel;
    roundRectPath(ctx, x - 6 * s, y - 6 * s, bw + 12 * s, bh + 28 * s, 6 * s);
    ctx.fill();
    ctx.fillStyle = 'rgba(255,255,255,0.1)';
    ctx.fillRect(x, y, bw, bh);
    const raged = boss.ph === 2;
    ctx.fillStyle = raged ? UI.white : UI.bossHp;
    ctx.fillRect(x, y, bw * clamp01(boss.hp / Math.max(1, boss.mhp)), bh);
    // The phase line at half: the bar shows how far it is to the rage.
    ctx.fillStyle = 'rgba(0,0,0,0.55)';
    ctx.fillRect(x + bw * BOSS.rageAt - s, y, 2 * s, bh);
    ctx.font = font(11 * s, 800);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = raged ? UI.bossHp : UI.text;
    const note = raged ? 'ENRAGED: stay dry when the frost ring grows' : 'immune to lightning';
    ctx.fillText(`JOTUNN  ${Math.max(0, Math.round(boss.hp))} / ${boss.mhp}  -  ${note}`, w / 2, y + bh + 11 * s);
  }

  /**
   * Mother Mire's bar: her phase lines at 60% and 25%, and what the phase asks
   * of you. It sits between the room panel (top left) and the buttons (top
   * right), so on a phone it narrows instead of running under them.
   */
  private drawMireBar(ctx: Ctx, boss: ViewModel['enemies'][number], boiling: boolean, w: number, s: number): void {
    const left = 254 * s, right = w - 130 * s;
    const bw = Math.max(160 * s, Math.min(420 * s, right - left)), bh = 14 * s;
    const x = Math.max(left, (left + right) / 2 - bw / 2), y = 18 * s;
    ctx.fillStyle = UI.panel;
    roundRectPath(ctx, x - 6 * s, y - 6 * s, bw + 12 * s, bh + 28 * s, 6 * s);
    ctx.fill();
    ctx.fillStyle = 'rgba(255,255,255,0.1)';
    ctx.fillRect(x, y, bw, bh);
    ctx.fillStyle = boss.str ? UI.white : UI.bossHp;
    ctx.fillRect(x, y, bw * clamp01(boss.hp / Math.max(1, boss.mhp)), bh);
    ctx.fillStyle = 'rgba(0,0,0,0.55)';
    for (const at of [MIRE.phase2At, MIRE.phase3At]) ctx.fillRect(x + bw * at - s, y, 2 * s, bh);
    const note = boss.sub && boiling ? 'her pool is boiling her'
      : boss.sub ? 'under her pool, healing: freeze it'
      : boss.str ? 'stranded on the ice: double damage'
      : boss.ph === 3 ? 'light the Everflame behind her'
      : boss.ph === 2 ? 'when she sinks, freeze her pool'
      : 'cross her beam with lightning';
    ctx.font = font(11 * s, 800);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = UI.text;
    ctx.fillText(`MOTHER MIRE  ${Math.max(0, Math.round(boss.hp))} / ${Math.round(boss.mhp)}  -  ${note}`, x + bw / 2, y + bh + 11 * s, bw + 8 * s);
  }

  drawPingFps(ctx: Ctx, vm: ViewModel, w: number, h: number, s: number, fps: number): void {
    ctx.font = font(10 * s, 600);
    ctx.textAlign = 'right';
    ctx.textBaseline = 'bottom';
    const ping = Math.round(vm.rtt);
    ctx.fillStyle = pingColor(ping);
    ctx.fillText(`${ping} ms`, w - 48 * s, h - 6 * s);
    ctx.fillStyle = UI.muted;
    ctx.fillText(`${Math.round(fps)} fps`, w - 8 * s, h - 6 * s);
  }

  // ---------------------------------------------------------------- centre: banners and status text

  drawBanners(ctx: Ctx, w: number, h: number, s: number): void {
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    let yOff = 0;
    for (const b of this.banners) {
      const inT = clamp01(b.t / 0.4);
      const scale = 2 - easeOutCubic(inT);
      const fade = b.t > b.dur - 0.5 ? (b.dur - b.t) / 0.5 : 1;
      ctx.globalAlpha = Math.min(1, inT * 2) * fade;
      ctx.font = font(44 * s * scale, 800);
      outlinedText(ctx, b.text, w / 2, h * 0.3 + yOff, b.color, 'rgba(0,0,0,0.85)', 6 * s);
      if (b.sub) {
        ctx.font = font(16 * s, 600);
        outlinedText(ctx, b.sub, w / 2, h * 0.3 + 34 * s + yOff, UI.text);
      }
      // Older banners sit above; leave room for the title (up to 2x) plus its sub-line.
      yOff += 96 * s;
    }
    ctx.globalAlpha = 1;
  }

  /** Countdown digit, "You died" with the revive hint, and the breather line. */
  drawCenter(ctx: Ctx, vm: ViewModel, me: LocalView | null, w: number, h: number, s: number, time: number): void {
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const wave = vm.wave;
    if (wave && wave.phase === 'countdown') {
      const n = Math.max(0, Math.ceil(wave.t));
      const frac = wave.t - Math.floor(wave.t);
      ctx.font = font(96 * s * (1 + 0.15 * frac), 800);
      ctx.globalAlpha = 0.5 + 0.5 * frac;
      outlinedText(ctx, `${n}`, w / 2, h * 0.42, UI.gold, 'rgba(0,0,0,0.85)', 8 * s);
      ctx.globalAlpha = 1;
    }
    if (me && me.dead) {
      const pulse = 0.85 + 0.15 * Math.sin(time * 3);
      ctx.fillStyle = rgba('#000000', 0.45);
      roundRectPath(ctx, w / 2 - 240 * s, h * 0.5 - 40 * s, 480 * s, 80 * s, 10 * s);
      ctx.fill();
      ctx.font = font(30 * s, 800);
      ctx.globalAlpha = pulse;
      outlinedText(ctx, 'You died', w / 2, h * 0.5 - 14 * s, UI.hpRed);
      ctx.globalAlpha = 1;
      ctx.font = font(13 * s, 600);
      outlinedText(ctx, 'An ally casts W A + Space near you to revive you. Everyone auto-revives next wave.', w / 2, h * 0.5 + 16 * s, UI.text);
      ctx.font = font(11 * s, 600);
      outlinedText(ctx, 'Spectating: move the mouse to the screen edge or use the arrow keys to look around.', w / 2, h * 0.5 + 32 * s, UI.muted);
    }
  }
}
